export type EpsonRasterPage = {
  width: number;
  height: number;
  data: string;
};

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function luminosity(rgba: Uint8ClampedArray, offset: number): number {
  const alpha = rgba[offset + 3]! / 255;
  const red = 255 + (rgba[offset]! - 255) * alpha;
  const green = 255 + (rgba[offset + 1]! - 255) * alpha;
  const blue = 255 + (rgba[offset + 2]! - 255) * alpha;
  return red * 0.299 + green * 0.587 + blue * 0.114;
}

export function rgbaToEpsonMonochrome(
  rgba: Uint8ClampedArray,
  width: number,
  sourceHeight: number,
): EpsonRasterPage {
  if (width <= 0 || sourceHeight <= 0 || width % 8 !== 0 || rgba.length !== width * sourceHeight * 4) {
    throw new Error("La página del ticket no tiene dimensiones válidas para la impresora.");
  }

  let lastInkRow = -1;
  for (let y = sourceHeight - 1; y >= 0 && lastInkRow < 0; y -= 1) {
    for (let x = 0; x < width; x += 1) {
      if (luminosity(rgba, (y * width + x) * 4) < 248) {
        lastInkRow = y;
        break;
      }
    }
  }

  // Conserva 24 puntos blancos al final para separar el contenido del corte.
  const height = Math.min(sourceHeight, Math.max(64, lastInkRow + 25));
  const rowBytes = width / 8;
  const raster = new Uint8Array(rowBytes * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (luminosity(rgba, (y * width + x) * 4) < 210) {
        const byteIndex = y * rowBytes + Math.floor(x / 8);
        raster[byteIndex] = raster[byteIndex]! | (0x80 >> (x % 8));
      }
    }
  }
  return { width, height, data: bytesToBase64(raster) };
}

export function buildEpsonTicketXml(pages: EpsonRasterPage[]): string {
  if (pages.length === 0) throw new Error("El documento no contiene tickets para imprimir.");
  const commands = pages.map((page) =>
    `<image width="${page.width}" height="${page.height}" color="color_1" mode="mono">${page.data}</image><cut type="feed" />`,
  ).join("");
  const request = `<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">${commands}</epos-print>`;
  return `<?xml version="1.0" encoding="utf-8"?><s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>${request}</s:Body></s:Envelope>`;
}
